import { motion } from 'framer-motion';
import { Search, Layers, MapPin, DoorOpen, Cpu, Check } from 'lucide-react';
import { useSceneTime, seg, EASE } from '../engine/sceneTime';
import { Words, Fade, Eyebrow } from '../components/Kinetic';
import { Vid, PhoneFrame } from '../components/Media';

// ── 07 AI IS LEARNING ───────────────────────────────────────────
const STRUCT_ICONS = [DoorOpen, Layers, MapPin, Search];

export function AILearning({ scene }) {
  const { t } = useSceneTime();
  const scanning = t >= 1.6 && t < 12.5;
  const structured = t >= 12.5;
  const scanP = seg(t, 1.6, 12.5);
  const pct = Math.round(scanP * 100);
  const beamTop = ((scanP * 2.5) % 1) * 100;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 140, width: 1000 }}>
        <Eyebrow show={t >= 0.4}>Video isn't just for people</Eyebrow>
        <Words text={scene.headline} show={t >= 0.8} size={112} lime={[2]} style={{ marginTop: 14 }} />
      </div>

      <motion.div className="pk-abs pk-glass pk-ai-log" style={{ left: 120, top: 380, width: 940 }} initial={false}
        animate={{ opacity: t >= 2.4 && !structured ? 1 : 0, y: t >= 2.4 && !structured ? 0 : 24 }} transition={{ duration: 0.7, ease: EASE }} data-testid="pk-ai-log">
        <div className="pk-ai-log-head"><Cpu size={18} /> nichecom · vision model <span className="pk-ai-live">● live</span></div>
        {scene.log.map((line, i) => {
          const p = seg(t, 3 + i * 1.7, 4.3 + i * 1.7);
          return (
            <div key={line.k} className={`pk-ai-log-line ${p > 0 ? 'on' : ''}`}>
              <span className="pk-ai-log-k">▸ {line.k}</span>
              <span className="pk-ai-log-v">{p >= 1 ? line.v : p > 0 ? '…' : ''}</span>
            </div>
          );
        })}
        <div className="pk-ai-log-bar"><motion.div initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.25, ease: 'linear' }} /></div>
      </motion.div>

      <div className="pk-abs" style={{ left: 120, top: 380, width: 940 }}>
        {scene.structure.map((s, i) => {
          const on = structured && t >= 12.5 + i * 0.5;
          const Icon = STRUCT_ICONS[i];
          const chips = i === 0 ? scene.labels.slice(0, 3) : i === 1 ? scene.labels.slice(3) : [];
          return (
            <motion.div key={s} className="pk-glass pk-struct" initial={false}
              animate={{ opacity: on ? 1 : 0, x: on ? 0 : -60 }} transition={{ duration: 0.8, ease: EASE }}>
              <div className="pk-struct-head"><span className="pk-struct-icon"><Icon size={22} /></span>{s}</div>
              {chips.length > 0 && <div className="pk-struct-chips">{chips.map((c) => <span key={c}>{c}</span>)}</div>}
            </motion.div>
          );
        })}
        <Fade show={t >= 16} y={14} className="pk-sub" style={{ marginTop: 20, fontSize: 30 }}>{scene.sub}</Fade>
      </div>

      <svg className="pk-abs" width={1920} height={1080} style={{ left: 0, top: 0, pointerEvents: 'none' }}>
        <motion.path d="M1220,560 C1150,560 1130,480 1060,480" fill="none" stroke="#96C11F" strokeWidth={2} strokeDasharray="6 14" className="pk-road-dash"
          initial={false} animate={{ opacity: scanning && t >= 2.4 ? 0.8 : 0 }} transition={{ duration: 0.6 }} />
      </svg>

      <motion.div className={`pk-abs pk-ai-portrait ${structured ? 'understood' : ''}`} style={{ left: 1220, top: 130, width: 460, height: 818 }}
        initial={false} animate={{ opacity: t >= 1 ? 1 : 0, scale: t >= 1 ? 1 : 0.94 }} transition={{ duration: 1, ease: EASE }} data-testid="pk-ai-portrait">
        <Vid video={scene.media.video} className="pk-fill" syncTime={t} />
        <div className="pk-ai-vignette" />
        <motion.div className="pk-ai-beam" initial={false} animate={{ top: `${beamTop}%`, opacity: scanning ? 1 : 0 }} transition={{ top: { duration: 0.1, ease: 'linear' }, opacity: { duration: 0.5 } }} />
        {scene.detections.map((d) => {
          const on = t >= d.from && t < d.to && !structured;
          const b = d.box;
          return (
            <motion.div key={d.label} className="pk-ai-box" style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }}
              initial={false} animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 1.15 }} transition={{ duration: 0.4, ease: EASE }}>
              <i className="tl" /><i className="tr" /><i className="bl" /><i className="br" />
              <span className="pk-ai-box-label"><span className="pk-ai-dot" />{d.label} <b>{d.conf}%</b></span>
            </motion.div>
          );
        })}
        <div className="pk-ai-corner tl" /><div className="pk-ai-corner tr" /><div className="pk-ai-corner bl" /><div className="pk-ai-corner br" />
        <div className="pk-ai-hud">
          <span className={`pk-ai-tag ${scanning ? 'blink' : ''} ${structured ? 'done' : ''}`}>{structured ? 'INDEXED' : scanning ? 'SCANNING' : 'STANDBY'}</span>
          <span className="pk-ai-pct">{pct}%</span>
        </div>
        <motion.div className="pk-ai-done" initial={false} animate={{ opacity: structured ? 1 : 0, y: structured ? 0 : 12 }} transition={{ duration: 0.6, ease: EASE }}>
          <span><Check size={18} /> Every frame understood</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

// ── 08 THE BIG QUESTION ─────────────────────────────────────────
const SUITE_RING = (count, rx, ry) => Array.from({ length: count }, (_, i) => {
  const a = -Math.PI / 2 + ((i + 0.5) / count) * Math.PI * 2;
  return { x: Math.cos(a) * rx, y: Math.sin(a) * ry };
});

export function Question({ scene }) {
  const { t } = useSceneTime();
  const fan = t >= 10;
  const video = scene.media.video;
  const suite = scene.suite || [];
  const ring = SUITE_RING(suite.length, 720, 270);
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 0, top: 100, width: 1920, textAlign: 'center' }}>
        <Words text={scene.headline} show={t >= 7} size={100} className="pk-words-center" lime={[6]} stagger={0.1} nowrap />
      </div>
      <div className="pk-abs" style={{ left: 960, top: 640 }}>
        <svg className="pk-abs" width={1920} height={1080} style={{ left: -960, top: -640, pointerEvents: 'none' }}>
          {ring.map((p, i) => (
            <motion.line key={i} x1={960} y1={640} x2={960 + p.x} y2={640 + p.y} stroke="rgba(150,193,31,0.35)" strokeWidth={1.5}
              initial={false} animate={{ pathLength: fan ? 1 : 0, opacity: fan ? 1 : 0 }} transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }} />
          ))}
        </svg>
        {suite.map((item, i) => {
          const p = ring[i];
          const on = t >= 10 + i * 0.32;
          return (
            <motion.div key={item.label} className="pk-abs pk-suite" style={{ left: -44, top: -78 }} initial={false}
              animate={{ x: on ? p.x : 0, y: on ? p.y : 0, opacity: on ? 1 : 0, scale: on ? 1 : 0.25 }}
              transition={on ? { type: 'spring', stiffness: 170, damping: 19, mass: 0.9 } : { duration: 0.5, ease: EASE }} data-testid={`pk-suite-${i}`}>
              <motion.div initial={false} animate={{ y: on ? [0, -6, 0] : 0 }} transition={{ duration: 4 + (i % 3), repeat: Infinity, ease: 'easeInOut' }}>
                <div className="pk-suite-tile"><img src={item.v.poster} alt="" /></div>
                <div className="pk-suite-label">{item.label}</div>
              </motion.div>
            </motion.div>
          );
        })}
        <motion.div className="pk-abs" style={{ left: -135, top: -258 }} initial={false}
          animate={{ opacity: t >= 0.4 ? 1 : 0, scale: t >= 0.4 ? (t >= 17 ? 1.06 : 1) : 0.8 }} transition={{ duration: 1.2, ease: EASE }}>
          <PhoneFrame width={270} glow>
            <Vid video={video} className="pk-fill" />
          </PhoneFrame>
          <Fade show={fan} y={8} className="pk-platform pk-suite-core">Sneak Peek · the capture</Fade>
        </motion.div>
      </div>
      <Fade show={t >= 16.5} y={10} className="pk-caption" style={{ position: 'absolute', left: 0, top: 1010, width: 1920, textAlign: 'center' }}>One capture. The whole suite.</Fade>
    </div>
  );
}

// ── 09 THE SOCIAL CONTENT PROBLEM ───────────────────────────────
const BAD_POSTS = [
  { k: 'stretch', x: 150, y: 300, r: -7 }, { k: 'clip', x: 520, y: 220, r: 4 }, { k: 'text', x: 900, y: 320, r: -3 },
  { k: 'stock', x: 1280, y: 230, r: 6 }, { k: 'tiny', x: 330, y: 640, r: 3 }, { k: 'fonts', x: 1120, y: 660, r: -5 },
];

function BadPost({ kind }) {
  if (kind === 'stretch') return <div className="pk-bad pk-bad-stretch"><div className="pk-bad-logo">HOMEFINDR</div><div className="pk-bad-img" /><div className="pk-bad-bar">NEW ON MARKET!!!</div></div>;
  if (kind === 'clip') return <div className="pk-bad pk-bad-clip"><div className="pk-bad-star">SOLD!</div><div className="pk-bad-img small" /><div className="pk-bad-row">Call us today 📞</div></div>;
  if (kind === 'text') return <div className="pk-bad pk-bad-text"><p>STUNNING 4 BED DETACHED FAMILY HOME IN SOUGHT AFTER LOCATION WITH GARAGE, GARDEN, OFF STREET PARKING, CLOSE TO SCHOOLS AND SHOPS AND TRANSPORT LINKS MUST BE SEEN TO BE APPRECIATED!!!</p><div className="pk-bad-img thin" /></div>;
  if (kind === 'stock') return <div className="pk-bad pk-bad-stock"><div className="pk-bad-stockimg">🤝</div><div className="pk-bad-bar alt">We sell houses</div></div>;
  if (kind === 'tiny') return <div className="pk-bad pk-bad-tiny"><div className="pk-bad-tinyimg" /><div className="pk-bad-copy">Guide price £450,000<br />3 bed · 2 bath · Garden<br />Viewings available this weekend</div></div>;
  return <div className="pk-bad pk-bad-fonts"><div className="f1">Open</div><div className="f2">HOUSE</div><div className="f3">Saturday</div><div className="pk-bad-img small" /></div>;
}

export function BadSocial({ scene }) {
  const { t } = useSceneTime();
  const hero = t >= 12;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 100, width: 1680 }}>
        <Words text={scene.headline} show={t >= 0.5 && !hero} hide={hero} size={82} lime={[2]} nowrap />
      </div>
      {BAD_POSTS.map((p, i) => {
        const on = t >= 2 + i * 1.4;
        return (
          <motion.div key={p.k} className="pk-abs" style={{ left: p.x, top: p.y }} initial={false}
            animate={{ opacity: on ? (hero ? 0.18 : 1) : 0, y: on ? 0 : 120, rotate: on ? p.r : 0, filter: hero ? 'blur(6px)' : 'blur(0px)', scale: hero ? 0.92 : 1 }}
            transition={{ duration: 0.9, ease: EASE }}>
            <BadPost kind={p.k} />
          </motion.div>
        );
      })}
      <motion.div className="pk-abs pk-hero-card" style={{ left: 1040, top: 250, width: 720, height: 560 }} initial={false}
        animate={{ opacity: hero ? 1 : 0, scale: hero ? 1 : 0.8, y: hero ? 0 : 80 }} transition={{ duration: 1.1, ease: EASE }}>
        <img src={scene.media.hero.url} alt="" />
        <div className="pk-hero-card-shade" />
        <div className="pk-hero-card-tag">Just Listed</div>
      </motion.div>
      <div className="pk-abs" style={{ left: 120, top: 330, width: 860 }}>
        <Words text={scene.overlay} show={hero} delay={0.3} size={108} lime={[4, 5]} />
      </div>
    </div>
  );
}

// ── 10 INTRODUCING SOCIAL PRO ───────────────────────────────────
export function Reveal({ scene }) {
  const { t } = useSceneTime();
  const glow = t >= 0.4;
  const statement = t >= 8;
  return (
    <div className="pk-fill">
      <motion.div className="pk-abs pk-reveal-glow" style={{ left: 460, top: 140, width: 1000, height: 800 }} initial={false}
        animate={{ opacity: glow ? 1 : 0, scale: glow ? (t >= 16 ? 1.15 : 1) : 0.3 }} transition={{ duration: 2.4, ease: EASE }} />
      <div className="pk-abs" style={{ left: 0, top: 0, width: 1920, height: 1080, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        {scene.products.map((name, i) => {
          const on = t >= 3 + i * 3.5;
          const [first, ...rest] = name.split(' ');
          return (
            <motion.div key={name} className="pk-product-word" initial={false}
              animate={{ opacity: on ? 1 : 0, letterSpacing: on ? '0.02em' : '0.5em', y: statement ? -40 : 0, scale: statement ? 0.9 : 1, filter: on ? 'blur(0px)' : 'blur(16px)' }}
              transition={{ duration: 1.3, ease: EASE }} data-testid={`pk-product-${i}`}>
              {first} <span className="pk-lime">{rest.join(' ')}</span>
            </motion.div>
          );
        })}
        <motion.div className="pk-line pk-line-glow" style={{ width: 420, height: 3, marginTop: 30 }} initial={false}
          animate={{ scaleX: statement ? 1 : 0, opacity: statement ? 1 : 0 }} transition={{ duration: 1, ease: EASE }} />
        <div style={{ marginTop: 40, textAlign: 'center' }}>
          <Words text={scene.statement[0]} show={statement} size={72} className="pk-words-center pk-light" />
          <Words text={scene.statement[1]} show={t >= 10.5} size={72} className="pk-words-center" lime={[2, 3]} />
        </div>
      </div>
    </div>
  );
}
